import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import {
  ClipboardCheck,
  ChevronDown,
  RotateCcw,
  Loader2,
  Info,
  PartyPopper,
  Wrench,
  CircleOff,
  Disc3,
  Droplets,
  Zap,
  Car,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_CONFIG = {
  engine: { label: 'Motor', icon: '\u{1F527}', lucide: Wrench },
  brakes: { label: 'Bremsen', icon: '\u{1F6D1}', lucide: CircleOff },
  tires: { label: 'Reifen', icon: '\u{1F6DE}', lucide: Disc3 },
  fluids: { label: 'Fl\u00FCssigkeiten', icon: '\u{1F4A7}', lucide: Droplets },
  electrical: { label: 'Elektrik', icon: '\u26A1', lucide: Zap },
  body: { label: 'Karosserie', icon: '\u{1F697}', lucide: Car },
  documents: { label: 'Dokumente', icon: '\u{1F4C4}', lucide: FileText },
};

const STORAGE_KEY_PREFIX = 'curvio_checklist_';

const PreTripChecklist = ({ vehicleType }) => {
  const [items, setItems] = useState([]);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [openCategories, setOpenCategories] = useState(new Set());

  const storageKey = `${STORAGE_KEY_PREFIX}${vehicleType || 'default'}`;

  // Load checked state from localStorage
  const loadCheckedState = useCallback(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        return new Set(JSON.parse(stored));
      }
    } catch (e) {
      console.error('Failed to load checklist state:', e);
    }
    return new Set();
  }, [storageKey]);

  // Save checked state to localStorage
  const saveCheckedState = useCallback(
    (checked) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify([...checked]));
      } catch (e) {
        console.error('Failed to save checklist state:', e);
      }
    },
    [storageKey]
  );

  const fetchChecklist = useCallback(async () => {
    try {
      setLoading(true);
      const type = vehicleType || 'oldtimer';
      const response = await axios.get(`${API}/checklist/${type}`);
      const data = Array.isArray(response.data) ? response.data : response.data.items || [];
      setItems(data);

      // Open all categories by default
      const categories = new Set(data.map((item) => item.category));
      setOpenCategories(categories);

      // Load saved state
      const saved = loadCheckedState();
      // Only keep IDs that still exist in the fetched items
      const validIds = new Set(data.map((item) => item.id));
      const filtered = new Set([...saved].filter((id) => validIds.has(id)));
      setCheckedIds(filtered);
    } catch (error) {
      console.error('Failed to fetch checklist:', error);
      toast.error('Checkliste konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [vehicleType, loadCheckedState]);

  useEffect(() => {
    fetchChecklist();
  }, [fetchChecklist]);

  const toggleItem = (id) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      saveCheckedState(next);
      return next;
    });
  };

  const handleReset = () => {
    setCheckedIds(new Set());
    saveCheckedState(new Set());
    toast.success('Checkliste zur\u00FCckgesetzt');
  };

  const toggleCategory = (category) => {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  // Group items by category
  const grouped = items.reduce((acc, item) => {
    const cat = item.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const totalItems = items.length;
  const checkedCount = [...checkedIds].filter((id) => items.some((item) => item.id === id)).length;
  const progressPercent = totalItems > 0 ? Math.round((checkedCount / totalItems) * 100) : 0;
  const isComplete = totalItems > 0 && checkedCount === totalItems;

  // Current season detection
  const currentMonth = new Date().getMonth(); // 0-11
  const getCurrentSeason = () => {
    if (currentMonth >= 2 && currentMonth <= 4) return 'spring';
    if (currentMonth >= 5 && currentMonth <= 7) return 'summer';
    if (currentMonth >= 8 && currentMonth <= 10) return 'autumn';
    return 'winter';
  };
  const currentSeason = getCurrentSeason();

  const SEASON_LABELS = {
    spring: 'Fr\u00FChling',
    summer: 'Sommer',
    autumn: 'Herbst',
    winter: 'Winter',
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-full bg-background">
      <div className="p-6 md:p-8 max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold text-foreground flex items-center gap-3">
              <ClipboardCheck className="w-8 h-8 text-primary" />
              Fahrzeug-Checkliste
            </h1>
            <p className="text-muted-foreground mt-2">
              {vehicleType === 'caravan' ? 'Caravan' : 'Oldtimer'} \u2014 Pr\u00FCfe vor jeder Fahrt
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="border-border text-muted-foreground hover:text-foreground"
            onClick={handleReset}
            disabled={checkedCount === 0}
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Alles zur\u00FCcksetzen
          </Button>
        </div>

        {/* Progress */}
        <Card className="bg-card border-border mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-foreground">Fortschritt</span>
              <span className="font-mono text-sm text-primary font-bold">
                {checkedCount}/{totalItems} ({progressPercent}%)
              </span>
            </div>
            <Progress
              value={progressPercent}
              className="h-3 bg-muted"
            />
          </CardContent>
        </Card>

        {/* Completion Message */}
        {isComplete && (
          <Card className="bg-green-500/10 border-green-500/30 mb-6">
            <CardContent className="p-6 text-center">
              <PartyPopper className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-green-500 mb-1">
                Alles gepr\u00FCft!
              </h3>
              <p className="text-sm text-green-500/80">
                Dein Fahrzeug ist bereit f\u00FCr die Reise. Gute Fahrt!
              </p>
            </CardContent>
          </Card>
        )}

        {/* Checklist Categories */}
        <ScrollArea className="h-[calc(100vh-380px)]">
          <div className="space-y-4 pr-4">
            {Object.entries(grouped).map(([category, categoryItems]) => {
              const config = CATEGORY_CONFIG[category] || {
                label: category,
                icon: '\u2705',
                lucide: ClipboardCheck,
              };
              const LucideIcon = config.lucide;
              const categoryChecked = categoryItems.filter((item) => checkedIds.has(item.id)).length;
              const categoryTotal = categoryItems.length;
              const allChecked = categoryChecked === categoryTotal;

              return (
                <Collapsible
                  key={category}
                  open={openCategories.has(category)}
                  onOpenChange={() => toggleCategory(category)}
                >
                  <Card className="bg-card border-border">
                    <CollapsibleTrigger className="w-full">
                      <CardHeader className="pb-0 cursor-pointer hover:bg-muted/20 transition-colors rounded-t-lg">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base text-foreground flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              allChecked ? 'bg-green-500/20' : 'bg-muted'
                            }`}>
                              <LucideIcon className={`w-4 h-4 ${allChecked ? 'text-green-500' : 'text-muted-foreground'}`} />
                            </div>
                            {config.label}
                            <Badge variant="outline" className={`text-xs ${
                              allChecked
                                ? 'bg-green-500/10 text-green-500 border-green-500/30'
                                : 'border-border text-muted-foreground'
                            }`}>
                              {categoryChecked}/{categoryTotal}
                            </Badge>
                          </CardTitle>
                          <ChevronDown
                            className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${
                              openCategories.has(category) ? 'rotate-180' : ''
                            }`}
                          />
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <CardContent className="pt-4 pb-2">
                        <div className="space-y-1">
                          {categoryItems.map((item) => {
                            const isChecked = checkedIds.has(item.id);
                            const isSeasonal = item.season && item.season !== 'all';
                            const isCurrentSeason = item.season === currentSeason;

                            return (
                              <div
                                key={item.id}
                                className={`flex items-start gap-3 p-3 rounded-lg transition-colors cursor-pointer ${
                                  isChecked
                                    ? 'bg-green-500/5'
                                    : 'hover:bg-muted/30'
                                }`}
                                onClick={() => toggleItem(item.id)}
                              >
                                <Checkbox
                                  checked={isChecked}
                                  onCheckedChange={() => toggleItem(item.id)}
                                  className="mt-0.5 border-border data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                                />

                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span
                                      className={`text-sm font-medium transition-colors ${
                                        isChecked ? 'text-muted-foreground line-through' : 'text-foreground'
                                      }`}
                                    >
                                      {item.label || item.name}
                                    </span>

                                    {isSeasonal && (
                                      <Badge
                                        variant="outline"
                                        className={`text-[10px] px-1.5 py-0 ${
                                          isCurrentSeason
                                            ? 'bg-amber-500/10 text-amber-500 border-amber-500/30'
                                            : 'border-border text-muted-foreground'
                                        }`}
                                      >
                                        {SEASON_LABELS[item.season] || item.season}
                                      </Badge>
                                    )}

                                    {item.description && (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Info className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 cursor-help" />
                                          </TooltipTrigger>
                                          <TooltipContent className="bg-card border-border text-foreground max-w-[250px]">
                                            <p className="text-xs">{item.description}</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              );
            })}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

export default PreTripChecklist;
